import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import { getMessages } from "@/i18n/runtime";
import {
  TIM_SKILL_DEFINITIONS,
  TIM_SKILL_HOLD_MS,
  type TimSkillDefinition,
  type TimSkillId,
} from "./timSkillDefinitions";
import type { TimSkillSnapshot } from "./timSkillModel";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import {
  compareHuge,
  huge,
  hugeZero,
  maxHuge,
  type HugeNumber,
} from "@/shared/numbers";

interface TimSkillViewOptions {
  readonly container: HTMLElement;
  readonly onAcquireRequest: (id: TimSkillId) => boolean;
}

interface IconNodes {
  readonly definition: TimSkillDefinition;
  readonly button: HTMLButtonElement;
  readonly fill: HTMLDivElement;
  readonly tooltipCost: HTMLDivElement;
  readonly tooltipState: HTMLDivElement;
  readonly chargeController: HoverChargeExecutionController;
}

const CELL_SIZE = 104;
const ICON_SIZE = 72;

export class TimSkillView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly tree: HTMLDivElement;
  private readonly svg: SVGSVGElement;
  private readonly onAcquireRequest: (id: TimSkillId) => boolean;
  private readonly iconNodes = new Map<TimSkillId, IconNodes>();
  private readonly cleanups: Array<() => void> = [];
  private lastTim: HugeNumber = hugeZero();
  private lastSnapshot: TimSkillSnapshot = { acquired: new Set() };

  constructor(options: TimSkillViewOptions) {
    const messages = getMessages();
    this.onAcquireRequest = options.onAcquireRequest;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-skill-panel hover-idle-tim-skill-panel";
    this.root.setAttribute("role", "region");
    this.root.setAttribute("aria-label", messages.timSkill.regionAria);
    this.root.hidden = true;

    const title = document.createElement("h2");
    title.textContent = messages.timSkill.title;

    this.tree = document.createElement("div");
    this.tree.className = "hov-skill-tree";

    const { maxRow, maxCol } = computeTreeExtent();
    this.tree.style.width = `${(maxCol + 1) * CELL_SIZE}px`;
    this.tree.style.height = `${(maxRow + 1) * CELL_SIZE}px`;

    this.svg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    ) as SVGSVGElement;
    this.svg.classList.add("hov-skill-tree-lines");
    this.svg.setAttribute(
      "viewBox",
      `0 0 ${(maxCol + 1) * CELL_SIZE} ${(maxRow + 1) * CELL_SIZE}`,
    );
    this.svg.setAttribute("aria-hidden", "true");
    this.tree.append(this.svg);

    this.drawPrerequisiteLines();

    for (const def of TIM_SKILL_DEFINITIONS) {
      this.createIcon(def);
    }

    this.root.append(title, this.tree);
    options.container.append(this.root);
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    if (!visible) {
      for (const nodes of this.iconNodes.values()) {
        this.cancelHoldFill(nodes);
      }
    }
  }

  update(params: {
    readonly tim: HugeNumber;
    readonly snapshot: TimSkillSnapshot;
  }): void {
    this.lastTim = params.tim;
    this.lastSnapshot = params.snapshot;
    for (const nodes of this.iconNodes.values()) {
      this.refreshIconState(nodes);
    }
  }

  dispose(): void {
    for (const nodes of this.iconNodes.values()) {
      this.cancelHoldFill(nodes);
      nodes.chargeController.dispose();
    }
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    this.iconNodes.clear();
    this.root.remove();
  }

  private createIcon(definition: TimSkillDefinition): void {
    const messages = getMessages();
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "hover-idle-charge-up-icon hover-idle-charge-up-icon-tim";
    button.dataset.skill = definition.id;
    button.dataset.virtualPointer = "allow";
    button.style.left = `${
      definition.position.col * CELL_SIZE + (CELL_SIZE - ICON_SIZE) / 2
    }px`;
    button.style.top = `${
      definition.position.row * CELL_SIZE + (CELL_SIZE - ICON_SIZE) / 2
    }px`;

    const clip = document.createElement("div");
    clip.className = "hover-idle-charge-up-icon-clip";

    const fill = document.createElement("div");
    fill.className = "hover-idle-charge-up-icon-fill";

    const label = document.createElement("span");
    label.className = "hover-idle-charge-up-icon-label";
    label.textContent = messages.timSkill.shortLabels[definition.id];

    clip.append(fill, label);

    const tooltip = document.createElement("div");
    tooltip.className = "hover-idle-charge-up-icon-tooltip";
    tooltip.setAttribute("role", "tooltip");

    const tooltipName = document.createElement("div");
    tooltipName.className = "hover-idle-charge-up-icon-tooltip-name";
    tooltipName.textContent = messages.timSkill.names[definition.id];

    const tooltipEffect = document.createElement("div");
    tooltipEffect.className = "hover-idle-charge-up-icon-tooltip-effect";
    tooltipEffect.textContent = `${messages.timSkill.effectLabel}: ${messages.timSkill.effects[definition.id]}`;

    const tooltipCost = document.createElement("div");
    tooltipCost.className = "hover-idle-charge-up-icon-tooltip-cost";
    tooltipCost.textContent = `${messages.timSkill.costLabel}: ${definition.costTim} Tim`;

    const tooltipState = document.createElement("div");
    tooltipState.className = "hover-idle-charge-up-icon-tooltip-state";

    tooltip.append(tooltipName, tooltipEffect, tooltipCost, tooltipState);
    button.append(clip, tooltip);
    this.tree.append(button);

    const nodes: IconNodes = {
      definition,
      button,
      fill,
      tooltipCost,
      tooltipState,
      chargeController: new HoverChargeExecutionController({
        getDurationMs: () => TIM_SKILL_HOLD_MS,
        canExecute: () => this.canAcquireSkillDefinition(definition),
        onProgress: (progress) => this.applyHoldFillProgress(fill, progress),
        onExecute: () => this.acquireSkillDefinition(definition, fill),
      }),
    };
    this.iconNodes.set(definition.id, nodes);

    const handleEnter = (): void => this.beginHoldFill(nodes);
    const handleLeave = (): void => this.releaseHoldFill(nodes);
    button.addEventListener("pointerenter", handleEnter);
    button.addEventListener("focus", handleEnter);
    button.addEventListener("pointerleave", handleLeave);
    button.addEventListener("blur", handleLeave);
    this.cleanups.push(() => {
      button.removeEventListener("pointerenter", handleEnter);
      button.removeEventListener("focus", handleEnter);
      button.removeEventListener("pointerleave", handleLeave);
      button.removeEventListener("blur", handleLeave);
    });
  }

  private drawPrerequisiteLines(): void {
    for (const def of TIM_SKILL_DEFINITIONS) {
      for (const prereqId of def.prerequisites) {
        const prereq = TIM_SKILL_DEFINITIONS.find((d) => d.id === prereqId);
        if (!prereq) continue;
        const line = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "line",
        );
        const fromX = prereq.position.col * CELL_SIZE + CELL_SIZE / 2;
        const fromY = prereq.position.row * CELL_SIZE + CELL_SIZE / 2;
        const toX = def.position.col * CELL_SIZE + CELL_SIZE / 2;
        const toY = def.position.row * CELL_SIZE + CELL_SIZE / 2;
        line.setAttribute("x1", String(fromX));
        line.setAttribute("y1", String(fromY));
        line.setAttribute("x2", String(toX));
        line.setAttribute("y2", String(toY));
        line.setAttribute("class", "hov-skill-tree-line");
        line.dataset.from = prereq.id;
        line.dataset.to = def.id;
        this.svg.append(line);
      }
    }
  }

  private refreshIconState(nodes: IconNodes): void {
    const messages = getMessages();
    const acquired = this.lastSnapshot.acquired.has(nodes.definition.id);
    const prereqMet = nodes.definition.prerequisites.every((req) =>
      this.lastSnapshot.acquired.has(req),
    );
    const affordable = compareHuge(this.lastTim, nodes.definition.costTim) >= 0;
    const ready = !acquired && prereqMet && affordable;

    nodes.button.dataset.acquired = acquired ? "true" : "false";
    nodes.button.dataset.prereqMet = prereqMet ? "true" : "false";
    nodes.button.dataset.affordable = affordable ? "true" : "false";
    nodes.button.dataset.ready = ready ? "true" : "false";
    nodes.button.disabled = acquired;

    let stateText: string;
    if (acquired) {
      stateText = messages.timSkill.stateAcquired;
    } else if (!prereqMet) {
      stateText = messages.timSkill.stateMissingPrerequisites;
    } else if (!affordable) {
      const missing = maxHuge(
        hugeZero(),
        huge(nodes.definition.costTim).sub(this.lastTim).ceil(),
      );
      stateText = messages.timSkill.stateMissingResource(
        formatForContext(missing, "tooltip"),
      );
    } else {
      stateText = messages.timSkill.stateHoldToAcquire(
        String(TIM_SKILL_HOLD_MS / 1000),
      );
    }
    nodes.tooltipState.textContent = stateText;
    nodes.tooltipState.dataset.acquired = acquired ? "true" : "false";
    nodes.tooltipState.dataset.ready = ready ? "true" : "false";

    if (acquired || !ready) {
      this.cancelHoldFill(nodes);
    }

    this.refreshLineState(nodes.definition.id, acquired);
  }

  private refreshLineState(id: TimSkillId, acquired: boolean): void {
    const lines = this.svg.querySelectorAll<SVGLineElement>(
      `line[data-from="${id}"]`,
    );
    for (const line of lines) {
      if (acquired) {
        line.dataset.active = "true";
      } else {
        delete line.dataset.active;
      }
    }
  }

  private beginHoldFill(nodes: IconNodes): void {
    if (!this.canAcquireSkillDefinition(nodes.definition)) return;
    nodes.chargeController.setHovered(true);
  }

  private releaseHoldFill(nodes: IconNodes): void {
    nodes.chargeController.setHovered(false);
    if (nodes.chargeController.getProgress() === 0) {
      this.resetFill(nodes);
    }
  }

  private cancelHoldFill(nodes: IconNodes): void {
    nodes.chargeController.cancel();
    this.resetFill(nodes);
  }

  private resetFill(nodes: IconNodes): void {
    this.resetFillElement(nodes.fill);
  }

  private canAcquireSkillDefinition(definition: TimSkillDefinition): boolean {
    const acquired = this.lastSnapshot.acquired.has(definition.id);
    if (acquired) return false;
    const prereqMet = definition.prerequisites.every((req) =>
      this.lastSnapshot.acquired.has(req),
    );
    if (!prereqMet) return false;
    return compareHuge(this.lastTim, definition.costTim) >= 0;
  }

  private applyHoldFillProgress(fill: HTMLDivElement, progress: number): void {
    fill.style.transition = "height 80ms linear";
    fill.style.height = `${progress * 100}%`;
  }

  private acquireSkillDefinition(
    definition: TimSkillDefinition,
    fill: HTMLDivElement,
  ): void {
    const succeeded = this.onAcquireRequest(definition.id);
    if (!succeeded) {
      this.resetFillElement(fill);
    }
  }

  private resetFillElement(fill: HTMLDivElement): void {
    fill.style.transition = "height 200ms ease-out";
    fill.style.height = "0%";
  }
}

function computeTreeExtent(): { maxRow: number; maxCol: number } {
  let maxRow = 0;
  let maxCol = 0;
  for (const def of TIM_SKILL_DEFINITIONS) {
    if (def.position.row > maxRow) maxRow = def.position.row;
    if (def.position.col > maxCol) maxCol = def.position.col;
  }
  return { maxRow, maxCol };
}
